import { TrimSpecs } from '@/lib/schema/build/types';
import { ValueWithUnit } from '../types';
import { parseChargingTime } from './chargingTime';
import { ChargerTypes } from './constants';
import { ChargingInfoType } from './schema/inventory/types';

type Rating = 'good' | 'bad' | 'average' | null;

export const getBatteryHealthRating = (
  batteryHealthPercentage: number
): Rating => {
  if (batteryHealthPercentage >= 70) {
    return 'good';
  }
  if (batteryHealthPercentage >= 40) {
    return 'average';
  }
  return 'bad';
};

export const getBatteryCapacityRating = (
  fuelType: string,
  capacity: number | null
): 'low' | 'mlow' | 'average' | 'mhigh' | 'high' | null => {
  if (!capacity) {
    return null;
  }
  if (fuelType === 'Electric') {
    if (capacity <= 30) {
      return 'low';
    }
    if (capacity > 30 && capacity <= 50) {
      return 'mlow';
    }
    if (capacity > 50 && capacity <= 75) {
      return 'average';
    }
    if (capacity > 75 && capacity <= 100) {
      return 'mhigh';
    }
    return 'high';
  }
  if (capacity <= 10) {
    return 'low';
  }
  if (capacity > 10 && capacity <= 12) {
    return 'mlow';
  }
  if (capacity > 12 && capacity <= 14) {
    return 'average';
  }
  if (capacity > 14 && capacity <= 16) {
    return 'mhigh';
  }
  return 'high';
};

export const getBatteryWarrantyRating = ({
  batteryWarranty,
  mileage,
  year,
}: {
  batteryWarranty?: {
    time: ValueWithUnit;
    distance: ValueWithUnit;
  };
  mileage?: ValueWithUnit;
  year?: number;
}) => {
  let warrantyString = 'N/A';
  let warrantyActive = null;
  let warrantyExpiringSoon = null;
  let remainingYears = null;
  let remainingMileage = null;

  if (batteryWarranty) {
    const warrantyYears =
      batteryWarranty.time.unit === 'month' ||
      batteryWarranty.time.unit === 'mo'
        ? Number(batteryWarranty?.time.value) / 12
        : Number(batteryWarranty?.time.value);

    const distanceValue = batteryWarranty.distance.value;
    const rawMiles = parseInt(String(distanceValue), 10);
    const mileageString = Number.isNaN(rawMiles)
      ? 'N/A mi'
      : `${rawMiles / 1000}K mi`;

    warrantyString = `${warrantyYears} yrs or ${mileageString}`;

    const mileageValue = Number(String(mileage?.value).replace(/,/g, ''));
    const batteryWarrantyValue = Number(
      String(batteryWarranty.distance.value).replace(/,/g, '')
    );

    if (mileage) {
      warrantyActive = mileageValue < batteryWarrantyValue;
    }
    if (year && !warrantyActive) {
      warrantyActive = new Date().getFullYear() - year < warrantyYears;
    }

    if (warrantyActive && mileage) {
      warrantyExpiringSoon = mileageValue + 20000 > batteryWarrantyValue;
      remainingMileage = batteryWarrantyValue - mileageValue;
    }
    if (warrantyActive && year) {
      if (!warrantyExpiringSoon) {
        warrantyExpiringSoon =
          new Date().getFullYear() - year < warrantyYears - 1;
      }
      remainingYears = warrantyYears - (new Date().getFullYear() - year);
    }
  }

  return {
    warrantyString,
    warrantyActive,
    warrantyExpiringSoon,
    remainingYears,
    remainingMileage,
  };
};

export const getRangeRating = ({
  rangeInfo,
  fuelType,
}: {
  rangeInfo: ValueWithUnit;
  fuelType: string;
}): Rating => {
  if (fuelType === 'Electric') {
    if (Number(rangeInfo.value) > 100) {
      return 'good';
    }
    if (Number(rangeInfo.value) > 30) {
      return 'average';
    }
    return 'bad';
  }
  // Phev
  if (Number(rangeInfo.value) > 50) {
    return 'good';
  }
  if (Number(rangeInfo.value) > 15) {
    return 'average';
  }
  return 'bad';
};

export const getChargingSpeedRating = ({
  chargingInfo,
}: {
  chargingInfo: ChargingInfoType;
}): Rating => {
  const level3 = parseChargingTime(chargingInfo.level3);
  if (!level3) {
    return null;
  }
  const { value, unit } = level3;
  let valueNumber = Number(value.replace(/,/g, ''));
  if (unit.toLowerCase() === 'hours') {
    valueNumber *= 60;
  }
  if (valueNumber < 40) {
    return 'good';
  }
  if (valueNumber < 60) {
    return 'average';
  }
  return 'bad';
};

export const getValueRating = ({
  listPrice,
  marketPrice,
}: {
  listPrice: number | null;
  marketPrice: number | null;
}): Rating => {
  if (marketPrice && listPrice) {
    if (listPrice <= marketPrice * 0.9) {
      return 'good';
    }
    if (listPrice < marketPrice * 1.1) {
      return 'average';
    }
    return 'bad';
  }
  return null;
};

export const getVehiclePower = ({
  trimSpecs,
}: {
  trimSpecs: TrimSpecs | null;
}): string => {
  const horsepower = trimSpecs?.highlights.Horsepower;
  const ePower = horsepower?.value
    ? `${Math.round(Number(horsepower.value) / 1.341).toLocaleString()} kw`
    : 'N/A';
  return ePower;
};

export const parseCharger = (rawType: string | undefined) => {
  if (rawType) {
    if (rawType.includes('j1772')) {
      return ChargerTypes.SAEJ1772;
    }
    if (rawType.includes('ccs')) {
      return ChargerTypes.CCS;
    }
    if (rawType.includes('nacs')) {
      return ChargerTypes.NACS;
    }
    if (rawType.includes('chademo')) {
      return ChargerTypes.CHAdeMo;
    }
  }
  return ChargerTypes.NA;
};

export const parseRawChargerTypes = ({
  chargingInfo,
  trimSpecs,
}: {
  chargingInfo: ChargingInfoType;
  trimSpecs: TrimSpecs | null;
}) => {
  const DCChargingRawType = chargingInfo.chargingConnectorDC
    .toLowerCase()
    .replace(/-/g, ' ');

  const ACChargerRawType = trimSpecs?.moreDetails['Powertrain and Mechanical']
    .find((detail) => detail.id === '25880')
    ?.value.toLowerCase()
    .replace(/-/g, ' ');

  return {
    DCCharger: parseCharger(DCChargingRawType),
    ACCharger: parseCharger(ACChargerRawType),
  };
};
